import * as React from 'react';
import { ValidationError } from '../../infrastructure/ValidationResult';
import { FormGroup, ControlLabel, FormControl, SelectFormControl } from '../bootstrap-components/BootstrapComponents';
import { Icon } from '../Icon';
import Dropzone = require('react-dropzone');
import * as DatePicker from 'react-bootstrap-date-picker';
import { getErrorMessage, formGroup, onControlChangedChange } from './FormFunctions';
import RichTextEditor from './RichTextEditor';
import { EditorState } from 'draft-js';

interface FormGroupProps {
    errorMessages: ValidationError[];
    label: string;
    name: string;
    value: any;
    placeholder?: string;
    disabled?: boolean;
    maxlength?: number;
    onChange: (name: string, value: string) => void;
    onKeyPress?: (e: any) => void;
    autoFocus?: boolean;
}

export function NumberFormGroup(props: FormGroupProps) {
    var formControl = (
        <FormControl
            type='number'
            value={props.value}
            placeholder={props.placeholder}
            name={props.name}
            onChange={x => onControlChangedChange(x, props.onChange)}
        />
    );
    var validationError = getErrorMessage(props.errorMessages, props.name);
    return formGroup(props.label, formControl, validationError);
}

export function TextFormGroup(props: FormGroupProps) {
    var formControl = (
        <FormControl
            type='text'
            placeholder={props.placeholder}
            value={props.value}
            name={props.name}
            disabled={props.disabled === undefined ? false : props.disabled}
            maxlength={props.maxlength}
            onChange={x => onControlChangedChange(x, props.onChange)}
            onKeyPress={props.onKeyPress}
            autoFocus={props.autoFocus}
        />
    );
    var validationError = getErrorMessage(props.errorMessages, props.name);
    return formGroup(props.label, formControl, validationError);
}

export function TextAreaFormGroup(props: FormGroupProps) {
    var formControl = (
        <FormControl
            type='textarea'
            placeholder={props.placeholder}
            value={props.value}
            name={props.name}
            disabled={props.disabled === undefined ? false : props.disabled}
            onChange={x => onControlChangedChange(x, props.onChange)}
        />
    );
    var validationError = getErrorMessage(props.errorMessages, props.name);
    return formGroup(props.label, formControl, validationError);
}

export function EmailFormGroup(props: FormGroupProps) {
    var formControl = (
        <FormControl
            type='email'
            placeholder={props.placeholder}
            value={props.value}
            name={props.name}
            disabled={props.disabled}
            autoFocus={props.autoFocus}
            onChange={x => onControlChangedChange(x, props.onChange)}
        />
    );
    var validationError = getErrorMessage(props.errorMessages, props.name);
    return formGroup(props.label, formControl, validationError);
}

export function PasswordFormGroup(props: FormGroupProps) {
    var formControl = (
        <FormControl
            type='password'
            placeholder={props.placeholder}
            value={props.value}
            name={props.name}
            disabled={props.disabled}
            onChange={x => onControlChangedChange(x, props.onChange)}
        />
    );
    var validationError = getErrorMessage(props.errorMessages, props.name);
    return formGroup(props.label, formControl, validationError);
}

export function DatePickerFormGroup(props: FormGroupProps) {
    var formControl = (
        <DatePicker
            value={props.value}
            calendarPlacement={props.placeholder}
            dateFormat='DD-MM-YYYY'
            weekStartsOnMonday={true}
            onChange={date => props.onChange(props.name, date)}
            dayLabels={['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør']}
            monthLabels={['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August',
                'September', 'Oktober', 'November', 'December']}
        />
    );
    var validationError = getErrorMessage(props.errorMessages, props.name);
    return formGroup(props.label, formControl, validationError);
}

export interface StaticTextFromGroupProps {
    label: string;
    value: string;
}
export function StaticTextFromGroup(props: StaticTextFromGroupProps) {
    return (
        <FormGroup>
            <ControlLabel>{props.label}</ControlLabel>
            <FormControl
                type='text'
                value={props.value}
                disabled={true}
            />
        </FormGroup>
    );
}

export interface RichTextFormGroupProps {
    label?: string;
    name?: string;
    placeholder?: string;
    onEditorChange?: (editorState: EditorState) => void;
    editorState: EditorState;
    errorMessages?: ValidationError[];
}
export function RichTextFormGroup(props: RichTextFormGroupProps) {
    const control = (
        <RichTextEditor
            onEditorChange={props.onEditorChange}
            editorState={props.editorState}
            placeholder={props.placeholder}
        />
    );

    var validationError = getErrorMessage(props.errorMessages, props.name);

    return formGroup(props.label === undefined ? '' : props.label, control, validationError);
}

export class MenuItemDto {
    key: any;
    value: string;

    constructor(key: any, value: string) {
        this.key = key;
        this.value = value;
    }
}
export interface DropDownFormGroupProps {
    errorMessages?: ValidationError[];
    name: string;
    label: string;
    selectedValue: any;
    items: MenuItemDto[];
    onChange: (name: string, value: string) => void;
    disabled?: boolean;
    ingenValgtText: string;
}
export function DropDownFormGroup(props: DropDownFormGroupProps) {
    const defaultValue = '-1';
    let isSelectedValueValid = false;
    let selectedVal = props.selectedValue === undefined ? '' : props.selectedValue.toString();
    const options = props.items.map((item, i) => {
        if (item.key.toString() === selectedVal) {
            isSelectedValueValid = true;
        }
        return <option key={i} value={item.key}>{item.value}</option>;
    });

    let value = selectedVal;

    if (!isSelectedValueValid || value === null || value === undefined || value.length === 0) {
        value = defaultValue;
    }

    let defaultOption: any;
    if (props.ingenValgtText !== undefined && props.ingenValgtText.length > 0) {
        defaultOption = <option value={defaultValue} disabled={true}>{props.ingenValgtText}</option>;
    }

    var formControl = (
        <SelectFormControl
            value={value}
            disabled={props.disabled === undefined ? false : props.disabled}
            onChange={(e: any) => props.onChange(props.name, e.target.value)}
        >
            {defaultOption}
            {options}
        </SelectFormControl>
    );

    var validationError = undefined;
    if (props.errorMessages) {
        validationError = getErrorMessage(props.errorMessages, props.name);
    }
    return formGroup(props.label, formControl, validationError);
}

export interface FileInputProps {
    errorMessages: ValidationError[];
    label: string;
    name: string;
    placeholder?: string;
    onFileUpload: (formData: FormData) => void;
}

class FileInput extends React.Component<FileInputProps, any> {
    constructor(props: FileInputProps) {
        super(props);

        this.uploadImage = this.uploadImage.bind(this);
    }

    uploadImage(acceptedFiles: any[]) {        
        if (acceptedFiles.length === 0) {
            throw new Error('Den valgte fil har et ugyldigt format.');
        } else if (acceptedFiles.length > 1) {
            throw new Error('Der kan kun uploades ét billede ad gangen.');
        }
        var formData = new FormData();
        formData.append('image', acceptedFiles[0]);

        this.props.onFileUpload(formData);
    }

    render() {

        var formControl = (
            <Dropzone
                multiple={false}
                accept='image/*'
                className={'form-control file-input'}
                onDrop={this.uploadImage}
            >
                {this.props.placeholder}
                <Icon className='file-input-icon' name='plus' />
            </Dropzone>
        );
        var validationError = getErrorMessage(this.props.errorMessages, this.props.name);

        return formGroup(this.props.label, formControl, validationError);
    }
}

export default FileInput;


// WEBPACK FOOTER //
// ./~/tslint-loader!./src/uiComponents/input/FormGroups.tsx